import { Canvas } from '@react-three/fiber'
import { TrackballControls } from '@react-three/drei'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Viewcube } from './ViewCube.js'
import { button, useControls } from 'leva'
import { Cloud } from './cloud.js'
import SentenceContext from './context/SentenceContext.js'
import DeviceContext from './context/DeviceContext.js'
import { Perf } from 'r3f-perf'
import languages from './languages.js'
import AppShell from './AppShell.js'
import { DialogProvider } from './context/DialogContext.js'
import UserContext from './context/UserContext.js'
import styled, { css, createGlobalStyle } from 'styled-components'
import Button from '@mui/material/Button'
import { LOGIN_MUTATION, REGISTER_MUTATION } from './query/gqlQueries.js'
import { useMutation } from '@apollo/client'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import InputAdornment from '@mui/material/InputAdornment'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LockIcon from '@mui/icons-material/Lock'
import DialogContent from '@mui/material/DialogContent'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './base.module.scss'
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText' // Import SplitText plugin
import { useGSAP } from '@gsap/react'

import logo from './assets/Vector.svg'

const languageOptions = Object.values(languages)
const shuffledLanguageOptions = Object.values(languages).sort(() => Math.random() - 0.5)
const owners = ['Shared', 'My', 'Tatoeba']
// const ownersExtended = ['My', 'Tatoeba', 'Others', 'Our']
// const labels = ['Sentences', 'Words', 'Conversations', 'Monologue', 'Thoughts', 'Projects', 'Self Improvement', 'Tasks', 'Inspiration']
const labels = ['Sentences']

export default function App() {
  // Active sentence and current translation
  const [activeSentenceObj, setActiveSentenceObj] = useState(null)
  const [activeTranslationObj, setActiveTranslationObj] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const [username, setUsername] = useState('')
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [registerUsername, setRegisterUsername] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [targetLanguage, setTargetLanguage] = useState('Portuguese')
  const [sourceLanguage, setSourceLanguage] = useState('English')
  const [owner, setOwner] = useState('My')
  const [label, setLabel] = useState('Sentences')
  const FormRef = useRef(null)
  const LogoRef = useRef(null)
  const ButtonRef = useRef(null)

  const [login] = useMutation(LOGIN_MUTATION)
  const [register] = useMutation(REGISTER_MUTATION)
  // New accounts are students unless the administrator says otherwise; only
  // owner/team accounts see the work tools.
  const [registerRole, setRegisterRole] = useState('student')

  const handleLogin = async () => {
    try {
      setLoading(true)
      const response = await login({ variables: { username: loginUsername, password: loginPassword } })
      const token = response.data.login.token
      localStorage.setItem('token', token) // Store the token in local storage
      setIsLoggedIn(true)
      setUsername(loginUsername)
      setLoading(false)
      setFeedbackMessage('Login successful!')
    } catch (error) {
      setLoading(true)
      setFeedbackMessage('Login failed: ' + error.message)
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    try {
      await register({ variables: { username: registerUsername, password: registerPassword, role: registerRole } })
      setFeedbackMessage(`Registration successful (${registerRole}). Please login.`)
    } catch (error) {
      setFeedbackMessage('Registration failed: ' + error.message)
    }
  }

  const [queryData, setQueryData] = useState([
    {
      sourceLanguage: 'eng',
      source: 'What do you want to learn today',
      sourceId: null,
      sourceType: null,
      targetLanguage: 'por',
      target: 'O que é que você quer aprender hoje?',
      targetId: null,
      targetType: null
    }
  ])

  // Change data into source and target sentence
  const handleData = (data) => {
    if (data.length === 0) {
      setQueryData([
        {
          sourceLanguage: 'eng',
          source: 'No matching sentences found',
          sourceId: null,
          sourceType: null,
          targetLanguage: 'por',
          target: 'Não foram encontradas frases correspondentes',
          targetId: null,
          targetType: null
        }
      ])
    } else {
      setQueryData(data)
    }
  }

  useEffect(() => {
    const checkIfMobile = () => {
      return (
        typeof window.orientation !== 'undefined' ||
        navigator.userAgent.indexOf('IEMobile') !== -1 ||
        window.matchMedia('only screen and (max-width: 767px)').matches ||
        navigator.userAgent.match(/iPad/i) !== null
      )
    }

    setIsMobile(checkIfMobile())
  }, [])

  let count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible

  if (process.env.REACT_APP_DEV === 'True') {
    // Assign values using useControls when in dev mode
    ;({ count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible } = useControls({
      count: { value: 4, min: 1, max: 16, step: 1 },
      radius: { value: 20, min: 10, max: 50, step: 1 },
      fontSize: { value: 1.2, min: 0.5, max: 5, step: 0.1 },
      primaryColor: { value: '#FFDE7D' },
      secondaryColor: { value: '#F2BE22' },
      tertiaryColor: { value: '#F29727' },
      hoverColor: { value: '#00ADB5' },
      voiceSpeed: { value: 0.75, min: 0.25, max: 1.5, step: 0.05 },
      removeFocus: button(() => resetActiveSentence()),
      perfVisible: false,
      tickerVisible: false
    }))
  } else {
    ;({ count, radius, fontSize, hoverColor, perfVisible, primaryColor, secondaryColor, tertiaryColor, voiceSpeed, tickerVisible } = {
      count: 4,
      radius: 20,
      fontSize: 1.2,
      primaryColor: '#FFDE7D',
      secondaryColor: '#F2BE22',
      tertiaryColor: '#F29727',
      hoverColor: '#00ADB5',
      voiceSpeed: 0.75,
      removeFocus: () => resetActiveSentence(),
      perfVisible: false,
      tickerVisible: false
    })
  }

  // Add the resetActiveSentence function
  const resetActiveSentence = () => {
    setActiveSentenceObj(null)
    setActiveTranslationObj(null)
  }

  useGSAP(() => {
    const tl = gsap.timeline()

    // Animate the logo into the center of the screen
    tl.from(LogoRef.current, {
      opacity: 0,
      y: -50,
      duration: 2,
      ease: 'power2.out'
    })

    // Animate the login form coming in from behind the logo to the right
    tl.from(
      FormRef.current,
      {
        opacity: 0,
        x: -50,
        duration: 1.5,
        ease: 'power2.out'
      },
      '-=0.5'
    ) // "-=1" overlaps the form animation with the logo animation by 1 second
    // Animate the login form coming in from behind the logo to the right
    tl.from(
      ButtonRef.current,
      {
        opacity: 0,
        y: +50,
        duration: 1.5,
        ease: 'power2.out'
      },
      '-=0.5'
    ) // "-=1" overlaps the form animation with the logo animation by 1 second
  }, [])

  if (!isLoggedIn) {
    const isRegister = process.env.REACT_APP_REGISTER === 'True'
    const handleKey = (e) => {
      if (e.key === 'Enter') handleLogin()
    }
    return (
      <LoginPage>
        <LoginCard>
          <LogoContainer ref={LogoRef}>
            <img src={logo} alt="logo" />
          </LogoContainer>

          <Heading>
            <h1>Welcome back</h1>
            <p>Sign in to continue learning</p>
          </Heading>

          <FormArea ref={FormRef}>
            <TextField
              autoFocus
              label="Username"
              margin="none"
              type="text"
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
              onKeyDown={handleKey}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircleIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
                  </InputAdornment>
                )
              }}
              sx={fieldSx}
            />
            <TextField
              label="Password"
              margin="none"
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={handleKey}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
                  </InputAdornment>
                )
              }}
              sx={fieldSx}
            />

            <ButtonDiv ref={ButtonRef}>
              <Button onClick={handleLogin} variant="contained" fullWidth disabled={loading} sx={loginBtnSx}>
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Login'}
              </Button>
            </ButtonDiv>

            {feedbackMessage ? <Feedback>{feedbackMessage}</Feedback> : null}
          </FormArea>

          {isRegister ? (
            <RegisterArea>
              <Divider>
                <span>or create an account</span>
              </Divider>
              <TextField
                label="New username"
                margin="none"
                type="text"
                value={registerUsername}
                onChange={(e) => setRegisterUsername(e.target.value)}
                fullWidth
                variant="outlined"
                sx={fieldSx}
              />
              <TextField
                label="New password"
                margin="none"
                type="password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                fullWidth
                variant="outlined"
                sx={fieldSx}
              />
              <TextField
                select
                label="Account type"
                margin="none"
                value={registerRole}
                onChange={(e) => setRegisterRole(e.target.value)}
                fullWidth
                variant="outlined"
                sx={fieldSx}
                helperText="Students never see the work tools"
              >
                <MenuItem value="student">Student</MenuItem>
                <MenuItem value="team">Team (work tools)</MenuItem>
              </TextField>
              <Button onClick={handleRegister} variant="outlined" fullWidth sx={registerBtnSx}>
                Register
              </Button>
            </RegisterArea>
          ) : null}
        </LoginCard>
      </LoginPage>
    )
  }

  return (
    <>
      <Canvas dpr={[1, 2]} camera={{ position: [0, 0, 35], fov: 90 }}>
        {perfVisible && <Perf position="top-left" />}
        <fog attach="fog" args={['#202025', 0, 80]} />
        <DeviceContext.Provider value={{ isMobile }}>
          <SentenceContext.Provider value={{ activeSentenceObj, setActiveSentenceObj, activeTranslationObj, setActiveTranslationObj }}>
            <Cloud
              count={count}
              radius={radius}
              fontSize={fontSize}
              sentenceList={queryData}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              tertiaryColor={tertiaryColor}
              hoverColor={hoverColor}
              targetLanguage={targetLanguage}
              sourceLanguage={sourceLanguage}
              label={label}
              owner={owner}
              voiceSpeed={voiceSpeed}
            />
          </SentenceContext.Provider>
        </DeviceContext.Provider>
        <TrackballControls />

        <Viewcube username={username} />
      </Canvas>

      <UserContext.Provider value={{ username, isLoggedIn }}>
        <DeviceContext.Provider value={{ isMobile }}>
          <SentenceContext.Provider value={{ activeSentenceObj, setActiveSentenceObj, activeTranslationObj, setActiveTranslationObj }}>
            <DialogProvider>
              <AppShell
                sourceLanguage={sourceLanguage}
                setSourceLanguage={setSourceLanguage}
                targetLanguage={targetLanguage}
                setTargetLanguage={setTargetLanguage}
                sentenceList={queryData}
                onData={handleData}
                label={label}
                setLabel={setLabel}
                owner={owner}
                setOwner={setOwner}
                labels={labels}
                owners={owners}
                languageOptions={languageOptions}
                tickerVisible={tickerVisible}
                voiceSpeed={voiceSpeed}
              />
            </DialogProvider>
          </SentenceContext.Provider>
        </DeviceContext.Provider>
      </UserContext.Provider>
    </>
  )
}

const SYSTEM_FONT = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`
const FIELD_BG = '#212127'

// Shared MUI overrides so the outlined fields read well on the dark page. A solid
// field colour + a webkit-autofill override kills the browser's yellow autofill
// box, so the icon and the input read as ONE continuous field (no curved seam).
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    borderRadius: '12px',
    backgroundColor: FIELD_BG,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: '1.5px' }
  },
  // Neutralise browser autofill (the yellow box) — paint the field colour over it.
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitTextFillColor: '#fff',
    caretColor: '#fff',
    WebkitBoxShadow: `0 0 0 1000px ${FIELD_BG} inset`,
    transition: 'background-color 9999s ease-in-out 0s'
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' }
}

const loginBtnSx = {
  mt: 0.5,
  py: 1.25,
  borderRadius: '12px',
  fontWeight: 600,
  fontSize: '0.95rem',
  textTransform: 'none',
  boxShadow: 'none',
  backgroundColor: '#2563eb',
  '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' }
}

const registerBtnSx = {
  py: 1.1,
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  color: 'rgba(255,255,255,0.85)',
  borderColor: 'rgba(255,255,255,0.2)',
  '&:hover': { borderColor: 'rgba(255,255,255,0.4)', backgroundColor: 'rgba(255,255,255,0.03)' }
}

const LoginPage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  font-family: ${SYSTEM_FONT};
  background: #1a1a1f;
`

const LoginCard = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
`

const LogoContainer = styled.div`
  img {
    width: 60px;
    height: 60px;
    display: block;
  }
`

const Heading = styled.div`
  text-align: center;
  margin-top: -4px;
  h1 {
    margin: 0;
    color: #fff;
    font-size: 1.45rem;
    font-weight: 600;
    letter-spacing: 0.2px;
  }
  p {
    margin: 6px 0 0;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
  }
`

const FormArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const ButtonDiv = styled.div`
  width: 100%;
`

const Feedback = styled.p`
  margin: 2px 0 0;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
`

const RegisterArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`
